import React, { useMemo } from 'react'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { Skeleton } from '@/components/ui/skeleton'
import { ServiceInformationProps } from '@/modules/Service/types'
import { getServiceConfigoptions } from '@/modules/Service/services'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    createSettingColumns,
    transformSettingData,
    getSettingRowClassName,
    hasCommitmentData,
    SettingRow,
} from '../../shared/setting-config'

const Setting: React.FC<ServiceInformationProps> = ({ service }) => {
    const { t } = useTranslation(['services', 'customer'])

    const { data, renderState, isLoading, isPending } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_CONFIGOPTIONS, service?.service_id],
        queryFn: () =>
            getServiceConfigoptions({
                serviceID: service?.service_id,
            }),
    })

    // Transform data into rows
    const rows = useMemo(
        () => transformSettingData({ data, service, t }),
        [data, service, t]
    )

    // Create columns - Vnis only shows name and commitment_value
    const columns = useMemo(
        () =>
            createSettingColumns({
                t,
                pricingModel: service?.service_pricing_model,
                peekSettings: ['name', 'commitment_value'],
                viewOnly: true,
            }),
        [t, service?.service_pricing_model]
    )

    // Handle loading/error states
    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (isLoading || isPending) {
        return (
            <div className="space-y-2 p-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
            </div>
        )
    }

    // Show PAYG message if no commitment data
    if (!hasCommitmentData(data)) {
        return (
            <div className="flex items-center justify-center p-8 bg-blue-50 rounded-md border border-blue-200 mt-2">
                <div className="text-center">
                    <p className="text-2xl font-semibold text-blue-700 mb-2">
                        {t('services:info.payg', { defaultValue: 'PAYG' })}
                    </p>
                    <p className="text-sm text-gray-600">
                        {t('services:info.payg-description', {
                            defaultValue:
                                'Pay As You Go - No commitment resources',
                        })}
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="mt-2">
            <DataTableCustomized<SettingRow>
                columns={columns}
                rows={rows}
                rowKey={(row) => row._rowId}
                rowClassName={getSettingRowClassName}
                size="small"
                toolbar={{
                    settings: {
                        show: true,
                        storageKey: 'vnis-setting',
                    },
                }}
            />
        </div>
    )
}

export default Setting
